import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { useShowToast } from '@/design_system/Toast';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useCanClaimRequest, useClaimRequest } from '@/models/request';
import { getUserStores } from '@/models/user';
import { useCurrentSession } from '@/services/auth';

export const RequestClaim = () => {
  const [params] = useSearchParams();

  const requestId = params.get('id');
  const clientToken = params.get('clientToken');
  const { data: canClaim, isError } = useCanClaimRequest({ requestId, clientToken });

  return (
    <>
      {canClaim?.status === 'has_access' && <Navigate to={`/requests/${requestId}`} />}
      {canClaim?.status === 'can_claim' && requestId && clientToken && (
        <ClaimDialog requestId={requestId} clientToken={clientToken} />
      )}
      {(canClaim?.status === 'cannot_access' || isError) && <AccessDeniedDialog />}
    </>
  );
};

const AccessDeniedDialog = () => {
  const navigate = useNavigate();

  return (
    <Dialog
      style={{ width: '25rem' }}
      isOpen
      title={<Trans id="request.claim.access-denied.title">Access denied</Trans>}
      onOpenChange={() => navigate('/requests', { replace: true })}
      dismissable={false}
    >
      <main>
        <Paragraph>
          <Trans id="request.claim.access-denied.content">
            You cannot take this request in charge or it doesn't exist anymore.
          </Trans>
        </Paragraph>
      </main>
      <Footer>
        <FooterButtons>
          <Button
            variant="secondary"
            size="medium"
            onPress={() => navigate('/requests', { replace: true })}
          >
            <Trans id="request.claim.access-denied.actions.ok">Ok</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};

const ClaimDialog = ({ requestId, clientToken }: { requestId: string; clientToken: string }) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { currentSession } = useCurrentSession();
  const showToast = useShowToast();

  // Maybe we'll need to handle the case where the user has roles on multiple stores
  const store = getUserStores(currentSession)?.[0];

  const { mutateAsync: claimRequest, isPending: isClaimRequestPending } = useClaimRequest({
    requestId: requestId,
  });

  return (
    <Dialog
      style={{ width: '25rem' }}
      isOpen
      title={<Trans id="request.claim.title">Take request in charge</Trans>}
      onOpenChange={() => navigate('/requests', { replace: true })}
      dismissable={false}
    >
      <main>
        <Paragraph>
          <Trans id="request.claim.content">
            This request was created by a client and doesn't belong to any store. Do you want to
            take it in charge on behalf of <strong>{store?.name}</strong>?
          </Trans>
        </Paragraph>
      </main>
      <Footer>
        <FooterButtons>
          <Button
            variant="secondary"
            size="medium"
            onPress={() => navigate('/requests', { replace: true })}
            disabled={isClaimRequestPending}
          >
            <Trans id="request.claim.actions.cancel">Cancel</Trans>
          </Button>
          <Button
            variant="primary"
            size="medium"
            isLoading={isClaimRequestPending}
            onPress={() => {
              claimRequest({
                clientToken: clientToken,
              }).then(() => {
                navigate(`/requests/${requestId}`, { replace: true });
                showToast({
                  text: t({
                    id: 'request.claim.actions.success',
                    message: 'The request has been successfully taken in charge by your store',
                  }),
                  type: 'success',
                });
              });
            }}
          >
            <Trans id="request.claim.actions.take-in-charge">Take in charge</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
